import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { useAuth } from '../hooks/useAuth';
import { canEditVenue, UserRole } from '@wineryxplorer/shared';
import type {
  Venue,
  PriceRange,
  PrimaryAtmosphere,
  Amenity,
  VenueStatus,
  ReservationPolicy,
} from '@wineryxplorer/shared';

import { PageContainer } from '../components/PageContainer';
import { useVenueFormValidation } from '../hooks/useVenueFormValidation';
import { formatTrpcError } from '../utils/formatTrpcError';

import { buildOperatingHours } from './WineryEdit/constants';
import type { OperatingHours, TabType, VenueType } from './WineryEdit/constants';
import { WineryEditHeader } from './WineryEdit/WineryEditHeader';
import { BasicInformationSection } from './WineryEdit/BasicInformationSection';
import { AddressSection } from './WineryEdit/AddressSection';
import { ContactInformationSection } from './WineryEdit/ContactInformationSection';
import type { SocialMediaValues } from './WineryEdit/ContactInformationSection';
import { FoodInformationSection } from './WineryEdit/FoodInformationSection';
import { VenueDetailsSection } from './WineryEdit/VenueDetailsSection';
import { OperatingHoursSection } from './WineryEdit/OperatingHoursSection';
import { MediaManagementTab } from './WineryEdit/MediaManagementTab';
import { MediaLibraryTab } from './WineryEdit/MediaLibraryTab';
import { AdminTab } from './WineryEdit/AdminTab';

const EMPTY_SOCIAL_MEDIA: SocialMediaValues = { instagram: '', facebook: '', youtube: '', tiktok: '' };

export function WineryEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const utils = trpc.useContext();
  const { user } = useAuth();

  const { data: venue, isPending, error } = trpc.venue.getById.useQuery(
    { id: id ?? '' },
    { enabled: !!id }
  );

  const updateVenue = trpc.venue.update.useMutation({
    onSuccess: () => {
      if (id) {
        utils.venue.getById.invalidate({ id });
        utils.venue.list.invalidate();
        validation.clearErrors();

        // Check if we came from the management dashboard
        const referrer = document.referrer;
        const isFromManagement = referrer.includes(`/wineries/${id}/manage`);

        if (isFromManagement) {
          navigate(`/wineries/${id}/manage`);
        } else {
          navigate(`/wineries/${id}`);
        }
      }
    },
    onError: (error: unknown) => {
      const formattedError = formatTrpcError(error);
      setSubmitError(formattedError);
    },
  });

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('details');

  // Form state
  const [name, setName] = useState('');
  const [venueType, setVenueType] = useState<VenueType>('winery');
  const [status, setStatus] = useState<VenueStatus>('published');
  const [description, setDescription] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [country, setCountry] = useState('USA');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [operatingHours, setOperatingHours] = useState<OperatingHours>(() => buildOperatingHours());
  const [operatingHoursNotes, setOperatingHoursNotes] = useState('');
  const [operatingHoursValidated, setOperatingHoursValidated] = useState<boolean>(false);
  const [descriptionSource, setDescriptionSource] = useState<string | null>(null);
  const [foodAvailableForPurchase, setFoodAvailableForPurchase] = useState<boolean>(false);
  const [foodDescription, setFoodDescription] = useState('');
  const [logoImageId, setLogoImageId] = useState<string | undefined>(undefined);
  const [heroImageIds, setHeroImageIds] = useState<string[]>([]);
  const [galleryImageIds, setGalleryImageIds] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<PriceRange | undefined>(undefined);
  const [primaryAtmosphere, setPrimaryAtmosphere] = useState<PrimaryAtmosphere | undefined>(undefined);
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [reservationPolicy, setReservationPolicy] = useState<ReservationPolicy>('recommended');
  const [bookingUrl, setBookingUrl] = useState('');
  const [socialMedia, setSocialMedia] = useState<SocialMediaValues>(EMPTY_SOCIAL_MEDIA);

  // Validation and error state
  const validation = useVenueFormValidation();
  const [submitError, setSubmitError] = useState<string>('');

  // Populate form when venue data loads
  useEffect(() => {
    if (venue) {
      setName(venue.name);
      setVenueType(venue.venueType);
      setStatus(venue.status);
      setDescription(venue.description ?? '');
      setStreet(venue.address.street);
      setCity(venue.address.city);
      setState(venue.address.state);
      setZipCode(venue.address.zipCode);
      setCountry(venue.address.country);
      setPhone(venue.phone ?? '');
      setEmail(venue.email ?? '');
      setWebsite(venue.website ?? '');
      setOperatingHours(buildOperatingHours(venue.operatingHours));
      setOperatingHoursNotes(venue.operatingHoursNotes ?? '');
      setOperatingHoursValidated(
        (venue as unknown as { operatingHoursValidated?: boolean }).operatingHoursValidated === true
      );
      setDescriptionSource(
        (venue as unknown as { descriptionSource?: string | null }).descriptionSource ?? null
      );
      setFoodAvailableForPurchase(venue.foodAvailableForPurchase ?? false);
      setFoodDescription(venue.foodDescription ?? '');
      setBookingUrl(venue.bookingUrl ?? '');
      setSocialMedia({ ...EMPTY_SOCIAL_MEDIA, ...venue.socialMedia });
      setReservationPolicy(
        ((venue as unknown as { reservationPolicy?: ReservationPolicy }).reservationPolicy ??
          'recommended') as ReservationPolicy
      );

      // Set media IDs from venue data
      setLogoImageId(venue.logoImageId ?? undefined);
      setHeroImageIds(venue.heroImageIds);
      setGalleryImageIds(venue.imageIds);

      // Set new enhancement fields
      setPriceRange((venue.priceRange ?? undefined) as PriceRange | undefined);
      setPrimaryAtmosphere((venue.primaryAtmosphere ?? undefined) as PrimaryAtmosphere | undefined);
      setAmenities(venue.amenities ?? []);
    }
  }, [venue]);

  const handleOperatingHoursChange = (
    day: string,
    field: 'open' | 'close' | 'closed',
    value: string | boolean
  ) => {
    setOperatingHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
      },
    }));
  };

  // Media management handlers
  const handleLogoChange = (logoId: string | null) => {
    setLogoImageId(logoId || undefined);
  };

  const handleHeroImagesChange = (heroIds: string[]) => {
    setHeroImageIds(heroIds);
  };

  const handleGalleryImagesChange = (galleryIds: string[]) => {
    setGalleryImageIds(galleryIds);
  };

  const handleSubmit = async () => {
    setSubmitError('');

    if (!id) return;

    // Validate the new enhancement fields
    const validationErrors = validation.validateAll({
      priceRange,
      primaryAtmosphere,
      amenities,
    });

    // If there are validation errors, don't submit
    if (Object.keys(validationErrors).length > 0) {
      setSubmitError('Please fix the validation errors before submitting.');
      return;
    }

    // Filter out incomplete operating hours entries
    const validOperatingHours: OperatingHours = {};
    Object.entries(operatingHours).forEach(([day, hours]) => {
      if (hours.closed || (hours.open && hours.close)) {
        validOperatingHours[day] = hours;
      }
    });

    const descriptionWasEdited = (venue?.description ?? '') !== description;
    const updateData = {
      id,
      name,
      venueType,
      status,
      description: description || undefined,
      // Editing the description text takes ownership and clears the AI Generated pill.
      // Untouched descriptions keep their existing source.
      ...(descriptionWasEdited ? { descriptionSource: 'manual' as const } : {}),
      address: {
        street,
        city,
        state,
        zipCode,
        country,
      },
      phone: phone || undefined,
      email: email || undefined,
      website: website || undefined,
      operatingHours: Object.keys(validOperatingHours).length > 0 ? validOperatingHours : undefined,
      operatingHoursNotes: operatingHoursNotes || undefined,
      operatingHoursValidated,
      foodAvailableForPurchase: foodAvailableForPurchase,
      foodDescription: foodDescription || undefined,
      logoImageId: logoImageId,
      heroImageIds: heroImageIds,
      imageIds: galleryImageIds,
      priceRange: priceRange ?? undefined,
      primaryAtmosphere: primaryAtmosphere ?? undefined,
      amenities: amenities ?? [],
      reservationPolicy,
      // '' (not undefined) so clearing a field actually clears it on the server.
      bookingUrl: bookingUrl.trim(),
      socialMedia: {
        instagram: socialMedia.instagram.trim(),
        facebook: socialMedia.facebook.trim(),
        youtube: socialMedia.youtube.trim(),
        tiktok: socialMedia.tiktok.trim(),
      },
    };

    updateVenue.mutate(updateData);
  };

  if (isPending) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl 2xl:max-w-6xl mx-auto">
        <PageContainer>
          <p className="text-gray-600">Loading venue...</p>
        </PageContainer>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl 2xl:max-w-6xl mx-auto">
        <PageContainer className="mt-2">
          <p className="text-red-600">Error loading venue: {error.message}</p>
        </PageContainer>
      </div>
    );
  }

  // Check if user has permission to edit this venue
  if (!user || !canEditVenue(venue as unknown as Venue, user.role, user.id)) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl 2xl:max-w-6xl mx-auto">
        <PageContainer className="mt-2">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h1 className="text-xl font-semibold text-red-800 mb-2">Access Denied</h1>
            <p className="text-red-700 mb-4">
              You don't have permission to edit this venue. Only site administrators, super administrators,
              and assigned venue administrators can edit venue details.
            </p>
          </div>
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl 2xl:max-w-6xl mx-auto">
      <PageContainer className="mt-2">
        <WineryEditHeader
          userRole={user.role}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onSave={handleSubmit}
          onCancel={() => navigate(`/wineries/${id}`)}
          isSaving={updateVenue.isPending}
          isValid={validation.isValid}
          hasValidationErrors={Object.keys(validation.errors).length > 0}
        />

        {/* Tab Content */}
        <div className="pt-6">
          {activeTab === 'details' && (
            <div>
              <BasicInformationSection
                name={name}
                onNameChange={setName}
                venueType={venueType}
                onVenueTypeChange={setVenueType}
                description={description}
                onDescriptionChange={setDescription}
                descriptionSource={descriptionSource}
              />

              <AddressSection
                street={street}
                onStreetChange={setStreet}
                city={city}
                onCityChange={setCity}
                state={state}
                onStateChange={setState}
                zipCode={zipCode}
                onZipCodeChange={setZipCode}
                country={country}
                onCountryChange={setCountry}
              />

              <ContactInformationSection
                phone={phone}
                onPhoneChange={setPhone}
                email={email}
                onEmailChange={setEmail}
                website={website}
                onWebsiteChange={setWebsite}
                socialMedia={socialMedia}
                onSocialMediaChange={(key, value) =>
                  setSocialMedia((prev) => ({ ...prev, [key]: value }))
                }
              />

              <FoodInformationSection
                foodAvailableForPurchase={foodAvailableForPurchase}
                onFoodAvailableForPurchaseChange={setFoodAvailableForPurchase}
                foodDescription={foodDescription}
                onFoodDescriptionChange={setFoodDescription}
              />

              <VenueDetailsSection
                submitError={submitError}
                priceRange={priceRange}
                onPriceRangeChange={(value) => {
                  setPriceRange(value);
                  validation.clearFieldError('priceRange');
                }}
                primaryAtmosphere={primaryAtmosphere}
                onPrimaryAtmosphereChange={(value) => {
                  setPrimaryAtmosphere(value);
                  validation.clearFieldError('primaryAtmosphere');
                }}
                amenities={amenities}
                onAmenitiesChange={(value) => {
                  setAmenities(value);
                  validation.clearFieldError('amenities');
                }}
                reservationPolicy={reservationPolicy}
                onReservationPolicyChange={setReservationPolicy}
                bookingUrl={bookingUrl}
                onBookingUrlChange={setBookingUrl}
                errors={validation.errors}
                isSaving={updateVenue.isPending}
              />

              <OperatingHoursSection
                operatingHours={operatingHours}
                onOperatingHoursChange={handleOperatingHoursChange}
                operatingHoursNotes={operatingHoursNotes}
                onOperatingHoursNotesChange={setOperatingHoursNotes}
                operatingHoursValidated={operatingHoursValidated}
                onOperatingHoursValidatedChange={setOperatingHoursValidated}
              />
            </div>
          )}

          {activeTab === 'media-management' && id && (
            <MediaManagementTab
              venueId={id}
              logoImageId={logoImageId}
              heroImageIds={heroImageIds}
              galleryImageIds={galleryImageIds}
              onLogoChange={handleLogoChange}
              onHeroImagesChange={handleHeroImagesChange}
              onGalleryImagesChange={handleGalleryImagesChange}
            />
          )}

          {activeTab === 'media' && id && <MediaLibraryTab venueId={id} />}

          {activeTab === 'admin' && user && (user.role === UserRole.SITE_ADMIN || user.role === UserRole.SUPER_ADMIN) && (
            <AdminTab status={status} onStatusChange={setStatus} />
          )}
        </div>
      </PageContainer>
    </div>
  );
}
